if (!customElements.get('oswaal-menu')) {
  customElements.define('oswaal-menu', class extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-omm-toggle]');
        if (!button || !this.contains(button)) return;
        this.expand(button.closest('.omm-item'), button.getAttribute('aria-expanded') !== 'true');
      }, options);
      this.addEventListener('keydown', event => {
        if (event.key !== 'Escape') return;
        let item = event.target.closest('.omm-item');
        let openItem = null;
        while (item && this.contains(item)) {
          if (this.button(item)?.getAttribute('aria-expanded') === 'true') { openItem = item; break; }
          item = item.parentElement.closest('.omm-item');
        }
        if (!openItem && this.dataset.mode === 'drawer') {
          const buttons = this.querySelectorAll('[data-omm-toggle][aria-expanded="true"]');
          openItem = buttons[buttons.length - 1]?.closest('.omm-item');
        }
        if (!openItem) return; // Dawn handles Escape for its outer drawer.
        this.skipEscapeUp = true;
        event.preventDefault();
        event.stopPropagation();
        const button = this.button(openItem);
        this.expand(openItem, false);
        button.focus();
      }, options);
      this.addEventListener('keyup', event => {
        if (event.key === 'Escape' && this.skipEscapeUp) {
          event.stopPropagation();
          this.skipEscapeUp = false;
        }
      }, options);
      this.breakpoint = matchMedia('(min-width: 990px)');
      this.breakpoint.addEventListener('change', () => {
        this.closeAll();
        const drawer = this.closest('header-drawer');
        if (drawer?.mainDetailsToggle?.open && drawer.dataset.breakpoint !== 'desktop') {
          drawer.closeMenuDrawer(new Event('resize'), drawer.mainDetailsToggle.querySelector('summary'));
        }
      }, options);
      const outer = this.closest('header-drawer')?.querySelector('details');
      outer?.addEventListener('toggle', () => { if (!outer.open) this.closeAll(); }, options);
      if (this.dataset.mode !== 'desktop') return;
      this.addEventListener('pointerover', event => {
        if (
          event.pointerType === 'touch' ||
          !matchMedia('(hover:hover) and (pointer:fine)').matches
        ) return;

        const row = event.target.closest('.omm-row');

        // Moving between elements in the same row:
        // keep the existing hover timer.
        if (row && row.contains(event.relatedTarget)) return;

        clearTimeout(this.categoryHoverTimer);

        // Entering the content area must not select another category.
        if (!row || !this.contains(row)) return;

        const item = row.closest('.omm-item');
        if (!item) return;

        const depth = Number(item.dataset.depth);

        if (depth === 2) {
          // Switch only if the pointer stays on this sidebar row.
          this.categoryHoverTimer = setTimeout(() => {
            if (this.isConnected && row.matches(':hover')) {
              this.selectCategory(item);
            }
          }, 300);
        } else if (depth === 1 && this.button(item)) {
          this.expand(item, true);
        }
      }, options);

      this.addEventListener('pointerout', event => {
        const row = event.target.closest('.omm-row');

        if (row && !row.contains(event.relatedTarget)) {
          clearTimeout(this.categoryHoverTimer);
        }
      }, options);

      this.addEventListener('pointerleave', () => {
        clearTimeout(this.categoryHoverTimer);

        if (!this.contains(document.activeElement)) {
          this.closeAll();
        }
      }, options);
      this.addEventListener('focusin', event => {
        // Category links select their column; disclosure buttons remain explicit toggles.
        if (!event.target.matches('a')) return;
        const category = event.target.closest('[data-depth="2"]');
        if (category) this.selectCategory(category);
      }, options);
      this.addEventListener('focusout', () => {
        queueMicrotask(() => { if (!this.contains(document.activeElement)) this.closeAll(); });
      }, options);
    }
    button(item) { return item.querySelector(':scope > .omm-row > [data-omm-toggle]'); }
    panel(item) { return item.querySelector(':scope > .omm-children'); }
    selectCategory(item) {
      Array.from(item.parentElement.children).forEach(sibling => this.setState(sibling, sibling === item));
    }
    setState(item, open) {
      const panel = this.panel(item);
      const button = this.button(item);
      if (!panel || !button) return;
      panel.hidden = !open;
      button.setAttribute('aria-expanded', String(open));
    }
    expand(item, open) {
      const depth = Number(item.dataset.depth);
      if (this.dataset.mode === 'desktop' && open && depth <= 2) {
        Array.from(item.parentElement.children).forEach(sibling => {
          if (sibling !== item) this.setState(sibling, false);
        });
      }
      this.setState(item, open);
      if (this.dataset.mode === 'desktop' && open && depth === 1) {
        const panel = this.panel(item);
        const firstCategory = panel?.querySelector('.omm-level-2 > .omm-item');
        if (firstCategory) this.selectCategory(firstCategory);
        this.style.setProperty('--omm-header-bottom', `${Math.max(0, Math.ceil(this.closest('.header-wrapper').getBoundingClientRect().bottom))}px`);
      }
      this.refresh();
    }
    refresh() {
      const outer = this.closest('header-drawer')?.querySelector('details');
      if (outer?.open && outer.contains(document.activeElement) && typeof trapFocus === 'function') {
        trapFocus(outer, document.activeElement);
      }
      if (this.dataset.mode !== 'desktop') return;
      const open = !!this.querySelector('.omm-level-1 > .omm-item > .omm-row > [aria-expanded="true"]');
      const sticky = this.closest('sticky-header');
      if (sticky) sticky.preventHide = open;
      if (open && !this.outside) {
        this.outside = new AbortController();
        document.addEventListener('pointerdown', event => {
          if (!this.contains(event.target)) this.closeAll();
        }, { capture: true, signal: this.outside.signal });
      } else if (!open) {
        this.outside?.abort();
        this.outside = null;
      }
    }
    closeAll() {
      clearTimeout(this.categoryHoverTimer);
      this.querySelectorAll('.omm-level-1 > .omm-item').forEach(item => this.setState(item, false));
      this.refresh();
    }
    disconnectedCallback() {
      this.closeAll();
      // Remove Dawn's scroll lock/trap if the editor removes an open Header.
      const drawer = this.closest('header-drawer');
      if (drawer?.mainDetailsToggle?.open) {
        drawer.closeMenuDrawer(new Event('section-unload'), drawer.mainDetailsToggle.querySelector('summary'));
      }
      this.events?.abort();
      this.outside?.abort();
      this.events = null;
      this.outside = null;
    }
  });
}
